import { useEffect, useRef } from 'react';
import { Alert, AppState, Linking, Platform } from 'react-native';
import * as Application from 'expo-application';

const APP_STORE_LOOKUP_URL = 'https://itunes.apple.com/lookup';
const APP_STORE_COUNTRY = 'au';
const SENTRY_IOS_APP_STORE_ID = '1484751651';
const SENTRY_IOS_STORE_URL = 'https://apps.apple.com/au/app/sentry/id1484751651';
const isDevelopment = process.env.NODE_ENV !== 'production';

const normalizeVersion = (version) => String(version || '')
	.split('.')
	.map(segment => Number.parseInt(segment, 10))
	.map(segment => (Number.isFinite(segment) ? segment : 0));

const isVersionGreater = (candidate, current) => {
	const candidateSegments = normalizeVersion(candidate);
	const currentSegments = normalizeVersion(current);
	const length = Math.max(candidateSegments.length, currentSegments.length);

	for (let index = 0; index < length; index += 1) {
		const candidateSegment = candidateSegments[index] || 0;
		const currentSegment = currentSegments[index] || 0;

		if (candidateSegment > currentSegment) {
			return true;
		}

		if (candidateSegment < currentSegment) {
			return false;
		}
	}

	return false;
};

const getIosAppStoreDetails = async () => {
	const response = await fetch(`${APP_STORE_LOOKUP_URL}?id=${SENTRY_IOS_APP_STORE_ID}&country=${APP_STORE_COUNTRY}`);
	const data = await response.json();
	const result = data?.results?.[0];

	return {
		version: result?.version,
		storeUrl: SENTRY_IOS_STORE_URL,
	};
};

const getStoreDetails = async () => {
	if (Platform.OS === 'ios') {
		return getIosAppStoreDetails();
	}

	// Google Play has no stable public version lookup; enable Android here once a backend version source exists.
	return null;
};

const openStoreUrl = async (storeUrl) => {
	if (!storeUrl) {
		return;
	}

	await Linking.openURL(storeUrl);
};

const useStoreUpdatePrompt = () => {
	const promptedVersionRef = useRef(null);
	const isCheckingRef = useRef(false);

	useEffect(() => {
		const shouldSkipPrompt = Platform.OS === 'web' || isDevelopment;

		if (shouldSkipPrompt) {
			return undefined;
		}

		const checkForStoreUpdate = async () => {
			if (isCheckingRef.current) {
				return;
			}

			const currentVersion = Application.nativeApplicationVersion;

			if (!currentVersion) {
				return;
			}

			isCheckingRef.current = true;

			try {
				const storeDetails = await getStoreDetails();
				const storeVersion = storeDetails?.version;
				const storeUrl = storeDetails?.storeUrl;

				if (!storeVersion || !storeUrl || !isVersionGreater(storeVersion, currentVersion) || promptedVersionRef.current === storeVersion) {
					return;
				}

				promptedVersionRef.current = storeVersion;

				Alert.alert(
					'Update available',
					'A new version of Sentry is available in the App Store. Do you want to update now?',
					[
						{
							text: 'Update',
							onPress: () => openStoreUrl(storeUrl),
						},
						{
							text: 'Cancel',
							style: 'cancel',
						},
					],
				);
			}
			catch {
				// Store lookups are best-effort and should not block the app.
			}
			finally {
				isCheckingRef.current = false;
			}
		};

		checkForStoreUpdate();

		const subscription = AppState.addEventListener('change', (nextAppState) => {
			if (nextAppState === 'active') {
				checkForStoreUpdate();
			}
		});

		return () => {
			subscription.remove();
		};
	}, []);
};

export default useStoreUpdatePrompt;
